import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Clock3,
  Gauge,
  MessageSquare,
  Users,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { alertLabel } from "../alert-badge";
import { displaySeverity, sortCalls, summaryOf } from "../lib/alerts";
import type {
  Headroom,
  SupervisedCall,
} from "../types";

type MixRow = { key: string; label: string; value: number; tone: string };

export interface LiveInsightsSnapshot {
  medianAgeSeconds: number | null;
  messages: number;
  callsWithActivity: number;
  floorLoad: number | null;
  riskMix: MixRow[];
  ageMix: MixRow[];
  assignmentMix: MixRow[];
  alertMix: MixRow[];
  attention: SupervisedCall[];
}

function ageSeconds(call: SupervisedCall, now: number): number | null {
  const started = call.created_at ? Date.parse(call.created_at) : NaN;
  if (!Number.isFinite(started)) return null;
  return Math.max(0, Math.floor((now - started) / 1000));
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const ordered = [...values].sort((a, b) => a - b);
  const middle = Math.floor(ordered.length / 2);
  return ordered.length % 2
    ? ordered[middle]
    : Math.round((ordered[middle - 1] + ordered[middle]) / 2);
}

export function formatLiveDuration(seconds: number | null): string {
  if (seconds == null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes < 60
    ? `${minutes}m ${String(remainder).padStart(2, "0")}s`
    : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/**
 * Derives only what the live roster can prove. This deliberately contains no "today" or
 * historical metrics: calls disappear from this dataset when they leave the floor.
 */
export function deriveLiveInsights(
  calls: SupervisedCall[],
  currentUserId?: string | null,
  headroom?: Headroom | null,
  now = Date.now(),
): LiveInsightsSnapshot {
  const ages = calls.map((call) => ageSeconds(call, now)).filter((age): age is number => age != null);
  const riskCounts = { red: 0, amber: 0, watch: 0, quiet: 0 };
  const ageCounts = { underMinute: 0, oneToThree: 0, threeToFive: 0, overFive: 0 };
  const assignmentCounts = { mine: 0, colleagues: 0, unassigned: 0 };
  const alertCounts = new Map<string, { count: number; call: SupervisedCall }>();

  calls.forEach((call) => {
    const severity = displaySeverity(call);
    const score = Number(call.risk?.score ?? 0);
    if (call.risk?.level === "red" || severity === "critical") riskCounts.red += 1;
    else if (call.risk?.level === "amber" || severity === "warn") riskCounts.amber += 1;
    else if (Number.isFinite(score) && score > 0) riskCounts.watch += 1;
    else riskCounts.quiet += 1;

    const age = ageSeconds(call, now);
    if (age != null) {
      if (age < 60) ageCounts.underMinute += 1;
      else if (age < 180) ageCounts.oneToThree += 1;
      else if (age < 300) ageCounts.threeToFive += 1;
      else ageCounts.overFive += 1;
    }

    if (!call.assigned_to) assignmentCounts.unassigned += 1;
    else if (currentUserId && call.assigned_to === currentUserId) assignmentCounts.mine += 1;
    else assignmentCounts.colleagues += 1;

    const top = summaryOf(call).top;
    if (top) {
      const current = alertCounts.get(top.type);
      alertCounts.set(top.type, { count: (current?.count ?? 0) + 1, call });
    }
  });

  const attention = sortCalls(calls).filter((call) => {
    const summary = summaryOf(call);
    return summary.overdue > 0 || displaySeverity(call) != null || Number(call.risk?.score ?? 0) > 0;
  }).slice(0, 8);

  return {
    medianAgeSeconds: median(ages),
    messages: calls.reduce((total, call) => total + Math.max(0, Number(call.message_count ?? 0) || 0), 0),
    callsWithActivity: calls.filter((call) => Number(call.message_count ?? 0) > 0).length,
    floorLoad: headroom?.capacity && headroom.capacity > 0 ? calls.length / headroom.capacity : null,
    riskMix: [
      { key: "red", label: "Red", value: riskCounts.red, tone: "bg-danger" },
      { key: "amber", label: "Amber", value: riskCounts.amber, tone: "bg-warning" },
      { key: "watch", label: "Watch", value: riskCounts.watch, tone: "bg-brand" },
      { key: "quiet", label: "Quiet", value: riskCounts.quiet, tone: "bg-success" },
    ],
    ageMix: [
      { key: "under-minute", label: "Under 1 minute", value: ageCounts.underMinute, tone: "bg-success" },
      { key: "one-three", label: "1–3 minutes", value: ageCounts.oneToThree, tone: "bg-brand" },
      { key: "three-five", label: "3–5 minutes", value: ageCounts.threeToFive, tone: "bg-warning" },
      { key: "over-five", label: "5+ minutes", value: ageCounts.overFive, tone: "bg-danger" },
    ],
    assignmentMix: [
      { key: "mine", label: "My slice", value: assignmentCounts.mine, tone: "bg-brand" },
      { key: "colleagues", label: "Colleagues", value: assignmentCounts.colleagues, tone: "bg-ink-3" },
      { key: "unassigned", label: "Unassigned", value: assignmentCounts.unassigned, tone: "bg-warning" },
    ],
    alertMix: [...alertCounts.entries()]
      .sort((a, b) => b[1].count - a[1].count)
      .slice(0, 6)
      .map(([type, entry]) => ({
        key: type,
        label: alertLabel(entry.call.alerts!.top!),
        value: entry.count,
        tone: entry.call.alerts?.top?.severity === "critical"
          ? "bg-danger"
          : entry.call.alerts?.top?.severity === "warn" ? "bg-warning" : "bg-brand",
      })),
    attention,
  };
}

function Metric({ label, value, detail, icon: Icon }: { label: string; value: string | number; detail: string; icon: typeof Activity }) {
  return <div className="border border-line bg-elevated px-3 py-2.5">
    <div className="flex items-center justify-between gap-2 text-[9px] font-semibold uppercase tracking-[0.14em] text-ink-3">
      <span>{label}</span><Icon size={13} />
    </div>
    <div className="mt-1 font-mono text-xl font-semibold tabular-nums text-ink">{value}</div>
    <div className="mt-0.5 truncate text-[10px] text-ink-3">{detail}</div>
  </div>;
}

function MixPanel({ title, rows, empty }: { title: string; rows: MixRow[]; empty?: string }) {
  const total = rows.reduce((sum, row) => sum + row.value, 0);
  return <section className="border border-line bg-elevated p-3">
    <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-3">{title}</h2>
    {total === 0 ? <p className="py-7 text-center text-xs italic text-ink-3">{empty ?? "No live data"}</p> : (
      <div className="mt-3 space-y-2.5">
        {rows.map((row) => (
          <div key={row.key}>
            <div className="mb-1 flex items-center justify-between text-[11px] text-ink-2">
              <span>{row.label}</span><span className="font-mono tabular-nums text-ink">{row.value}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-soft">
              <div className={cn("h-full rounded-full", row.tone)} style={{ width: `${Math.max(3, (row.value / total) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    )}
  </section>;
}

export function LiveInsights({ calls, currentUserId, headroom, onOpenCall }: {
  calls: SupervisedCall[];
  currentUserId?: string | null;
  headroom?: Headroom | null;
  onOpenCall: (callId: string) => void;
}) {
  const snapshot = deriveLiveInsights(calls, currentUserId, headroom);
  const urgent = snapshot.riskMix[0].value + snapshot.riskMix[1].value;
  const overdue = calls.reduce((total, call) => total + summaryOf(call).overdue, 0);
  const load = snapshot.floorLoad == null ? "—" : `${Math.round(snapshot.floorLoad * 100)}%`;

  return <div className="min-h-0 flex-1 overflow-y-auto p-2 sm:p-3" data-testid="supervision-live-insights">
    <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
      <div>
        <h1 className="text-sm font-semibold text-ink">Live floor insights</h1>
        <p className="mt-0.5 text-[11px] text-ink-3">Current roster snapshot · updates as calls enter and leave the floor</p>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="rounded-full border border-line bg-elevated px-2.5 py-1 text-[10px] font-medium text-ink-3">No historical reporting</span>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-px bg-line md:grid-cols-3 xl:grid-cols-6">
      <Metric label="Live now" value={calls.length} detail="calls on the floor" icon={Activity} />
      <Metric label="Needs attention" value={urgent} detail="red or amber calls" icon={AlertTriangle} />
      <Metric label="Overdue" value={overdue} detail="open overdue alerts" icon={Clock3} />
      <Metric label="Median live time" value={formatLiveDuration(snapshot.medianAgeSeconds)} detail="current calls only" icon={Clock3} />
      <Metric label="Transcript activity" value={snapshot.messages} detail={`${snapshot.callsWithActivity} calls speaking`} icon={MessageSquare} />
      <Metric label="Floor load" value={load} detail={headroom?.capacity ? `${calls.length} / ${headroom.capacity} capacity` : "capacity unavailable"} icon={Gauge} />
    </div>

    <div className="mt-2 grid gap-2 lg:grid-cols-2 xl:grid-cols-4">
      <MixPanel title="Live risk mix" rows={snapshot.riskMix} />
      <MixPanel title="Call age" rows={snapshot.ageMix} />
      <MixPanel title="Floor assignment" rows={snapshot.assignmentMix} />
      <MixPanel title="Top visible alerts" rows={snapshot.alertMix} empty="No alert signals on the live roster" />
    </div>

    <section className="mt-2 border border-line bg-elevated">
      <div className="flex items-center justify-between border-b border-line px-3 py-2">
        <div>
          <h2 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ink-3">Attention now</h2>
          <p className="mt-0.5 text-[10px] text-ink-3">Ranked from the same live signals as the wall</p>
        </div>
        <Users size={14} className="text-ink-3" />
      </div>
      {snapshot.attention.length ? <div className="grid gap-px bg-line md:grid-cols-2 xl:grid-cols-4">
        {snapshot.attention.map((call) => {
          const top = summaryOf(call).top;
          const severity = displaySeverity(call);
          return <button
            key={call.call_id}
            type="button"
            onClick={() => onOpenCall(call.call_id)}
            className="group flex min-w-0 items-center gap-2 bg-elevated px-3 py-2.5 text-left transition hover:bg-soft"
          >
            <span className={cn("h-2 w-2 shrink-0 rounded-full", severity === "critical" ? "bg-danger" : severity === "warn" ? "bg-warning" : "bg-brand")} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs font-semibold text-ink">{call.contact_name || "Unknown caller"}</span>
              <span className="block truncate text-[10px] text-ink-3">{top ? alertLabel(top) : `Risk ${Math.round(Number(call.risk?.score ?? 0))}`}</span>
            </span>
            <ArrowUpRight size={13} className="shrink-0 text-ink-3 transition group-hover:text-brand" />
          </button>;
        })}
      </div> : <p className="px-3 py-8 text-center text-xs text-ink-3">No live calls currently need attention.</p>}
    </section>

    <p className="mt-2 text-[10px] leading-4 text-ink-3">
      Alert mix uses the highest-priority visible alert on each call. Completed-call trends, outcomes, and intervention history need retained reporting data and are intentionally not inferred here.
    </p>
  </div>;
}
